import { createAsyncThunk } from '@reduxjs/toolkit';
import { axiosInstanceNewAPI } from '@/services/api/utils/axiosInstance';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import { getError } from '@/utils/error-report/getError';
import { permalinkSchema, Permalink } from '@/models/permalinkSchema';
import { pageableSchema } from '@/models/pageableSchema';
import { ThunkConfig } from '@/types/store';
import { apiPath } from '../apiPath';
import {
  PERMALINK_CREATING_ERROR_PREFIX,
  PERMALINK_FETCHING_ERROR_PREFIX,
  PERMALINK_UPDATING_ERROR_PREFIX,
} from './permalinks.constants';

export const getPermalinks = createAsyncThunk<Permalink[], void, ThunkConfig>(
  'permalinks/getPermalinks',
  async () => {
    try {
      const response = await axiosInstanceNewAPI.get(apiPath.getPermalinks());
      const isDataValid = validateDataUsingZodSchema(
        response.data,
        pageableSchema(permalinkSchema),
      );
      return isDataValid ? response.data.content : [];
    } catch (error) {
      return Promise.reject(getError({ error, prefix: PERMALINK_FETCHING_ERROR_PREFIX }));
    }
  },
);

export const createPermalink = createAsyncThunk<Permalink, { url: string }, ThunkConfig>(
  'permalinks/createPermalink',
  async ({ url }) => {
    try {
      const response = await axiosInstanceNewAPI.post<Permalink>(apiPath.createPermalink(), {
        url,
      });
      const isDataValid = validateDataUsingZodSchema(response.data, permalinkSchema);
      if (!isDataValid) {
        return Promise.reject(new Error(PERMALINK_CREATING_ERROR_PREFIX));
      }
      return response.data;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: PERMALINK_CREATING_ERROR_PREFIX }));
    }
  },
);

export const updatePermalink = createAsyncThunk<
  Permalink,
  { id: number; url: string },
  ThunkConfig
>('permalinks/updatePermalink', async ({ id, url }) => {
  try {
    const response = await axiosInstanceNewAPI.put<Permalink>(apiPath.updatePermalink(id), { url });
    const isDataValid = validateDataUsingZodSchema(response.data, permalinkSchema);
    if (!isDataValid) {
      return Promise.reject(new Error(PERMALINK_UPDATING_ERROR_PREFIX));
    }
    return response.data;
  } catch (error) {
    return Promise.reject(getError({ error, prefix: PERMALINK_UPDATING_ERROR_PREFIX }));
  }
});
